import * as React from 'react'

import { Alert, Box, Card, RadioButton, Text } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import { TimeSettingsEnumsTimeTypeEnum as TimeTypeEnum } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import Footer from 'modules/time_categories_settings/components/WorkTimeCategories/CategoryModal/Footer'
import { ActionType } from 'modules/time_categories_settings/components/utils/actionTypes'
import type { CustomTimeRangeCategoryFormType } from 'modules/time_categories_settings/lib/forms/CreateCustomTimeRangeCategoryForm'

interface Props {
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  form: FormikProps<CustomTimeRangeCategoryFormType>
  actionType: ActionType
}

interface FormErrors {
  name: string
  timeType: string
}

const CategoryModal = ({ setModalOpen, actionType, form }: Props) => {
  const { values: formValues, errors: formErrors } = form
  const [missingError, setMissingError] = React.useState({
    name: false,
    timeType: false,
  })

  const isUpdate = actionType === 'update'
  const timeTypeInitialState = isUpdate ? formValues.timeType : undefined
  const [timeType, setTimeType] = React.useState<TimeTypeEnum | undefined>(timeTypeInitialState)

  const title = isUpdate
    ? i18n.t('time_categories.actions.edit')
    : i18n.t('time_categories.actions.add')

  const onChangeTimeType = (value: TimeTypeEnum) => {
    setTimeType(value)
    setMissingError((prevState) => ({ ...prevState, timeType: false }))
    form.setFieldValue('timeType', value)
  }

  const onChangeName = (value: string) => {
    setMissingError((prevState) => ({ ...prevState, name: false }))
    form.setFieldValue('name', value)
  }

  const validateForm = (formValues: CustomTimeRangeCategoryFormType) => {
    const errors: FormErrors = {
      name: !formValues.name ? i18n.t('time_categories.modals.errors.missing_name') : '',
      timeType: !timeType ? i18n.t('time_categories.modals.errors.missing_type') : '',
    }
    return errors
  }

  const onSubmit = () => {
    const errors = validateForm(form.values)
    const hasErrors = Object.values(errors).some(Boolean)

    if (!hasErrors) {
      form.handleSubmit()
    } else {
      setMissingError({
        name: !!errors.name,
        timeType: !!errors.timeType,
      })
    }
  }

  const errorNameMessage =
    formErrors.name ||
    (missingError.name && i18n.t('time_categories.modals.errors.missing_name')) ||
    ''

  const onClose = () => {
    form.resetForm()
    setMissingError({
      name: false,
      timeType: false,
    })
    setModalOpen(false)
  }

  return (
    <Modal
      title={title}
      illustration="timer"
      description={i18n.t('time_categories.modals.description')}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={<Footer onClose={onClose} disabled={form.isSubmitting} />}
    >
      <TextField
        label={i18n.t('common.name')}
        error={errorNameMessage}
        mandatory
        value={formValues.name}
        onChange={onChangeName}
      />
      <Box paddingBottom="s4" paddingTop="s16">
        <Text size="200" weight="medium" color="grey1000">
          {i18n.t('time_categories.modals.time_type')}
        </Text>
      </Box>
      <Box gap="s16">
        <Card onClick={isUpdate ? undefined : () => onChangeTimeType('regular')}>
          <Card.Title
            size="small"
            action={
              <RadioButton
                size="medium"
                onChange={() => onChangeTimeType('regular')}
                value={form.values.timeType}
                checked={timeType === 'regular'}
                disabled={isUpdate}
              />
            }
          >
            {i18n.t('time_categories.regular')}
          </Card.Title>
          <Card.Description>{i18n.t('time_categories.regular_description')}</Card.Description>
        </Card>
        <Card onClick={isUpdate ? undefined : () => onChangeTimeType('overtime')}>
          <Card.Title
            size="small"
            action={
              <RadioButton
                size="medium"
                onChange={() => onChangeTimeType('overtime')}
                value={form.values.timeType}
                checked={timeType === 'overtime'}
                disabled={isUpdate}
              />
            }
          >
            {i18n.t('time_categories.overtime')}
          </Card.Title>
          <Card.Description>{i18n.t('time_categories.overtime_description')}</Card.Description>
        </Card>
        <Card onClick={isUpdate ? undefined : () => onChangeTimeType('any_time')}>
          <Card.Title
            size="small"
            action={
              <RadioButton
                size="medium"
                onChange={() => onChangeTimeType('any_time')}
                value={form.values.timeType}
                checked={timeType === 'any_time'}
                disabled={isUpdate}
              />
            }
          >
            {i18n.t('time_categories.any_time')}
          </Card.Title>
          <Card.Description>{i18n.t('time_categories.any_time_description')}</Card.Description>
        </Card>

        {missingError.timeType && (
          <Box paddingLeft="s2">
            <Text size="200" color="radicalRed1000" capitalize>
              {i18n.t('time_categories.modals.errors.missing_type')}
            </Text>
          </Box>
        )}
      </Box>
      {formErrors.name && (
        <Box paddingTop="s24">
          <Alert color="error">{i18n.t('time_categories.modals.errors.duplicated_name')}</Alert>
        </Box>
      )}
    </Modal>
  )
}

export default CategoryModal
